import { useMemo, useRef, useState } from 'react';
import { useStore } from '@tanstack/react-form';
import { noop } from 'lodash-es';
import { useTranslation } from '@frontend/i18n';
import { FullscreenModal, NakedButton, SpinningLoader, Text, TextLink } from '@frontend/design-system';
import { formatFieldLabel } from '../../../../../../helpers';
import { useFillingExperienceContext } from '../../../../provider';
import type { DynamicFormApi } from '../../hooks/use-app-form';
import { reportSignatureError } from '../../signature/monitoring';
import { useMediaBlob } from '../../signature/use-media-blob';
import type { DynamicFormField } from '../../types';
import {
  fieldErrorStyle,
  fieldsetStyle,
  statusBoxStyle,
  thumbnailButtonStyle,
  thumbnailFrameStyle,
  thumbnailTextStyle,
} from './pdf-field.styles';
import { PdfPageCanvas } from './pdf-page-canvas';
import { PdfSignEditor } from './pdf-sign-editor';
import { getSignedAttachmentId, type PdfFieldValue } from './pdf-value';
import { PdfViewer } from './pdf-viewer';
import { usePdfDocument } from './use-pdf-document';

const THUMBNAIL_WIDTH = 96;

const getOriginalAttachmentId = (field: DynamicFormField) =>
  typeof field.fieldMeta.originalAttachmentId === 'string' ? field.fieldMeta.originalAttachmentId : '';

export interface PdfFieldProps {
  form: DynamicFormApi;
  fieldInfo: DynamicFormField;
}

export const PdfField = ({ form, fieldInfo }: PdfFieldProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const { isPreviewMode, documentId } = useFillingExperienceContext();
  const fieldKey = fieldInfo.key;
  const originalId = getOriginalAttachmentId(fieldInfo);
  const eSignRequired = !!fieldInfo.fieldMeta.eSignRequired;

  const fieldErrors = useStore(form.store, (state) => {
    const meta = state.fieldMeta[fieldKey];
    if (!meta?.isTouched) return [];
    return (meta?.errors ?? []) as string[];
  });

  const validate = ({ value }: { value: unknown }) => {
    if (isPreviewMode) return undefined;
    if (eSignRequired && !getSignedAttachmentId(value)) return t('Please sign this document to proceed further.');
    return undefined;
  };

  return (
    <fieldset css={fieldsetStyle} role='group' aria-required={eSignRequired} aria-labelledby={`${fieldKey}-label`}>
      {fieldInfo.label && (
        <Text id={`${fieldKey}-label`} weight='bold'>
          {formatFieldLabel(fieldInfo.label, eSignRequired)}
        </Text>
      )}

      <form.AppField name={fieldKey} validators={{ onChange: validate }}>
        {(field) => (
          <PdfInput
            fieldKey={fieldKey}
            title={fieldInfo.label || t('Document')}
            formId={documentId}
            originalId={originalId}
            signedId={getSignedAttachmentId(field.state.value)}
            eSignRequired={eSignRequired}
            hasError={fieldErrors.length > 0}
            onCommit={(signedAttachmentId) => field.handleChange({ signedAttachmentId } satisfies PdfFieldValue)}
            onClose={() => field.handleBlur()}
          />
        )}
      </form.AppField>

      {fieldErrors.length > 0 && (
        <Text size='small' css={fieldErrorStyle} role='alert'>
          {fieldErrors.join(', ')}
        </Text>
      )}
    </fieldset>
  );
};

type PdfSourceErrorReason = 'download' | 'password' | 'invalid' | 'timeout';

/** Downloads (or reuses the local copy of) a PDF media id and parses it with pdf.js. */
const usePdfSource = (mediaId: string, { fieldKey, formId }: { fieldKey: string; formId?: string }) => {
  const media = useMediaBlob(mediaId, {
    onError: (error) => reportSignatureError(error, { feature: 'pdf-sign', operation: 'download', fieldKey, formId }),
  });
  // A fresh Blob reference makes `usePdfDocument` parse again (used to retry after a timeout).
  const [attempt, setAttempt] = useState(0);
  const blob = useMemo(() => (media.blob && attempt ? media.blob.slice() : media.blob), [media.blob, attempt]);
  const pdf = usePdfDocument(blob, {
    onError: (error) => reportSignatureError(error, { feature: 'pdf-sign', operation: 'load', fieldKey, formId }),
  });

  const errorReason: PdfSourceErrorReason | undefined = media.isError ? 'download' : pdf.errorReason;
  return {
    blob,
    doc: pdf.doc,
    pages: pdf.pages,
    isLoading: media.isLoading || pdf.status === 'loading',
    errorReason,
    canRetry: errorReason === 'download' || errorReason === 'timeout',
    retry: () => (media.isError ? void media.refetch() : setAttempt((value) => value + 1)),
  };
};

type PdfSource = ReturnType<typeof usePdfSource>;

const usePdfErrorMessage = (reason: PdfSourceErrorReason | undefined) => {
  const { t } = useTranslation('digital-forms-experience');
  if (!reason) return undefined;
  if (reason === 'password') return t('This document is password protected and cannot be displayed.');
  if (reason === 'timeout') return t('The document is taking too long to load.');
  return t('Unable to load the document!');
};

type ModalMode = 'closed' | 'view' | 'sign';

interface PdfInputProps {
  fieldKey: string;
  title: string;
  formId?: string;
  originalId: string;
  signedId: string;
  eSignRequired: boolean;
  hasError: boolean;
  onCommit: (signedAttachmentId: string) => void;
  onClose: () => void;
}

const PdfInput = ({
  fieldKey,
  title,
  formId,
  originalId,
  signedId,
  eSignRequired,
  hasError,
  onCommit,
  onClose,
}: PdfInputProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const [mode, setMode] = useState<ModalMode>('closed');
  const isGeneratingRef = useRef(false);
  const isSigned = !!signedId;
  const isEditable = eSignRequired && !isSigned;
  const source = usePdfSource(signedId || originalId, { fieldKey, formId });

  const handleClose = () => {
    // The signed copy is being generated/uploaded; closing now would drop it.
    if (isGeneratingRef.current) return;
    setMode('closed');
    onClose();
  };

  const handleCommit = (signedAttachmentId: string) => {
    onCommit(signedAttachmentId);
    setMode('closed');
    onClose();
  };

  const renderRenderError = (error: unknown) =>
    reportSignatureError(error, { feature: 'pdf-sign', operation: 'render', fieldKey, formId });

  if (!originalId && !signedId) {
    return (
      <div css={statusBoxStyle}>
        <Text size='medium' color='light'>
          {t('No document attached.')}
        </Text>
      </div>
    );
  }

  return (
    <>
      <PdfThumbnail
        id={fieldKey}
        source={source}
        description={
          isEditable
            ? t('Please read the attached document carefully and sign it.')
            : t('Please read the attached document carefully.')
        }
        isSigned={isSigned}
        hasError={hasError}
        onOpen={() => setMode(isEditable ? 'sign' : 'view')}
        onRenderError={renderRenderError}
      />

      <FullscreenModal show={mode !== 'closed'} onClose={handleClose}>
        <FullscreenModal.Header title={title} />
        {mode === 'view' && source.doc && (
          <PdfReadOnlyView
            source={source}
            // Re-signing stamps the original; a signed copy restored without one can only be viewed.
            canResign={eSignRequired && isSigned && !!originalId}
            onResign={() => setMode('sign')}
            onRenderError={renderRenderError}
          />
        )}
        {mode === 'sign' && (
          <PdfSignSession
            fieldKey={fieldKey}
            formId={formId}
            originalId={originalId}
            // While unsigned the displayed document is the original, so reuse it instead of parsing twice.
            preloaded={isSigned ? undefined : source}
            onCommit={handleCommit}
            onGeneratingChange={(isGenerating) => (isGeneratingRef.current = isGenerating)}
            onRenderError={renderRenderError}
          />
        )}
      </FullscreenModal>
    </>
  );
};

interface PdfThumbnailProps {
  id: string;
  source: PdfSource;
  description: string;
  isSigned: boolean;
  hasError?: boolean;
  onOpen: () => void;
  onRenderError: (error: unknown) => void;
}

/** First-page thumbnail that opens the fullscreen viewer, or the load/error state while it isn't available. */
const PdfThumbnail = ({ id, source, description, isSigned, hasError, onOpen, onRenderError }: PdfThumbnailProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const errorMessage = usePdfErrorMessage(source.errorReason);

  if (errorMessage) {
    return (
      <div id={id} css={statusBoxStyle} role='alert' tabIndex={-1}>
        <Text size='medium'>{errorMessage}</Text>
        {source.canRetry && (
          <TextLink size='medium' weight='bold' onClick={source.retry}>
            {t('Retry')}
          </TextLink>
        )}
      </div>
    );
  }

  if (!source.doc || source.isLoading) {
    return (
      <div id={id} css={statusBoxStyle} aria-busy tabIndex={-1}>
        <SpinningLoader size='small' />
        <Text size='medium' color='light'>
          {t('Loading document...')}
        </Text>
      </div>
    );
  }

  return (
    <NakedButton id={id} css={thumbnailButtonStyle} aria-invalid={hasError} aria-haspopup='dialog' onClick={onOpen}>
      <div css={thumbnailFrameStyle}>
        {source.pages[0] && (
          <PdfPageCanvas
            doc={source.doc}
            pageIndex={0}
            pageSize={source.pages[0]}
            width={THUMBNAIL_WIDTH}
            ariaLabel={t('Document preview')}
            onRenderError={onRenderError}
          />
        )}
      </div>
      <span css={thumbnailTextStyle}>
        <Text as='span' size='medium'>
          {description}
        </Text>
        <Text as='span' size='small' color='light'>
          {t('(Click on the document to view it in fullscreen mode)')}
        </Text>
        <Text as='span' size='small' color='light'>
          {isSigned
            ? t('Signed · {{count}} pages', { count: source.pages.length })
            : t('{{count}} pages', { count: source.pages.length })}
        </Text>
      </span>
    </NakedButton>
  );
};

interface PdfReviewPreviewProps {
  field: DynamicFormField;
  title: string;
}

/** Read-only PDF for review mode: the signed copy when there is one, else the original. No re-signing. */
export const PdfReviewPreview = ({ field, title }: PdfReviewPreviewProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const { documentId } = useFillingExperienceContext();
  const [isOpen, setIsOpen] = useState(false);
  const fieldKey = field.key;
  const originalId = getOriginalAttachmentId(field);
  const signedId = getSignedAttachmentId(field.value);
  const source = usePdfSource(signedId || originalId, { fieldKey, formId: documentId });

  const renderRenderError = (error: unknown) =>
    reportSignatureError(error, { feature: 'pdf-sign', operation: 'render', fieldKey, formId: documentId });

  if (!originalId && !signedId) {
    return (
      <Text size='medium' color='light'>
        {t('No document attached.')}
      </Text>
    );
  }

  return (
    <>
      <PdfThumbnail
        id={`${fieldKey}-review`}
        source={source}
        description={t('Please read the attached document carefully.')}
        isSigned={!!signedId}
        onOpen={() => setIsOpen(true)}
        onRenderError={renderRenderError}
      />
      <FullscreenModal show={isOpen} onClose={() => setIsOpen(false)}>
        <FullscreenModal.Header title={title} />
        {isOpen && source.doc && (
          <PdfReadOnlyView source={source} canResign={false} onResign={noop} onRenderError={renderRenderError} />
        )}
      </FullscreenModal>
    </>
  );
};

interface PdfReadOnlyViewProps {
  source: PdfSource;
  canResign: boolean;
  onResign: () => void;
  onRenderError: (error: unknown) => void;
}

const PdfReadOnlyView = ({ source, canResign, onResign, onRenderError }: PdfReadOnlyViewProps) => {
  const { t } = useTranslation('digital-forms-experience');
  if (!source.doc) return null;

  return (
    <PdfViewer
      doc={source.doc}
      pages={source.pages}
      onRenderError={onRenderError}
      toolbarActions={
        canResign && (
          <TextLink size='medium' weight='bold' onClick={onResign}>
            {t('Re-sign document')}
          </TextLink>
        )
      }
    />
  );
};

interface PdfSignSessionProps {
  fieldKey: string;
  formId?: string;
  originalId: string;
  preloaded?: PdfSource;
  onCommit: (signedAttachmentId: string) => void;
  onGeneratingChange: (isGenerating: boolean) => void;
  onRenderError: (error: unknown) => void;
}

const PdfSignSession = ({ preloaded, ...props }: PdfSignSessionProps) =>
  preloaded ? <PdfSignSessionContent source={preloaded} {...props} /> : <PdfSignSessionLoader {...props} />;

const PdfSignSessionLoader = (props: Omit<PdfSignSessionProps, 'preloaded'>) => {
  const source = usePdfSource(props.originalId, { fieldKey: props.fieldKey, formId: props.formId });
  return <PdfSignSessionContent source={source} {...props} />;
};

const PdfSignSessionContent = ({
  source,
  fieldKey,
  formId,
  onCommit,
  onGeneratingChange,
  onRenderError,
}: Omit<PdfSignSessionProps, 'preloaded'> & { source: PdfSource }) => {
  const { t } = useTranslation('digital-forms-experience');
  const errorMessage = usePdfErrorMessage(source.errorReason);

  if (errorMessage || !source.doc || !source.blob) {
    return (
      <FullscreenModal.Body>
        <div css={statusBoxStyle} role={errorMessage ? 'alert' : undefined} aria-busy={!errorMessage}>
          {errorMessage ? (
            <>
              <Text size='medium'>{errorMessage}</Text>
              {source.canRetry && (
                <TextLink size='medium' weight='bold' onClick={source.retry}>
                  {t('Retry')}
                </TextLink>
              )}
            </>
          ) : (
            <>
              <SpinningLoader size='small' />
              <Text size='medium' color='light'>
                {t('Loading document...')}
              </Text>
            </>
          )}
        </div>
      </FullscreenModal.Body>
    );
  }

  return (
    <PdfSignEditor
      fieldKey={fieldKey}
      formId={formId}
      originalBlob={source.blob}
      doc={source.doc}
      pages={source.pages}
      onCommit={onCommit}
      onGeneratingChange={onGeneratingChange}
      onRenderError={onRenderError}
    />
  );
};
